/**
 * A single vendored vector glyph, rendered centered in the diamond's upper
 * half. Dormant plumbing: no HAZARD_CLASS_DIAMOND entry currently sets
 * `icon` (see below) — real DOT pictogram artwork is a design follow-up, not
 * something this file should approximate. A flame silhouette was tried here
 * and read as a water drop at the diamond's small render size, so it was
 * pulled rather than kept as a misleading placeholder.
 */
export interface HazardClassIcon {
  path: string;
  viewBox: { width: number; height: number };
}

/**
 * DOT hazard-class placard color + name, derived (not stored) from
 * LabelContent.dotHazardClass. Text color follows the placard convention of
 * dark text on light placards and white text on saturated ones. The name is
 * rendered outside the diamond (a secondary line under the shipping name),
 * not inside it — only the class number goes in the diamond itself.
 *
 * Single source of truth for both the pdf-generator lambda (the printed
 * label) and Oak's LabelPreview (the live editing preview) — the two must
 * never disagree on which classes get a colored diamond and which render
 * text-only, since the preview's job is to show what will actually print.
 *
 * `icon` stays part of the type (dormant — no entry sets it) for when real
 * DOT pictogram artwork is available; see the note on HazardClassIcon above.
 */
export type HazardClassDiamondEntry = {
  name: string;
  /**
   * Absent for classes whose real placard is striped or split (4.1's
   * red/white stripes, 4.2's white-over-red, 8's white-over-black, 9's
   * striped top half): a solid-fill stand-in would be a *wrong* DOT marking
   * on a document a customer may treat as compliant, which is worse than no
   * diamond. Those classes render only the "Class X ..." text line. Also
   * absent for the bare "2"/"6" entries, where the division (and so the
   * placard) is unknown.
   */
  fill?: { color: string; textColor: string };
  icon?: HazardClassIcon;
};

export const HAZARD_CLASS_DIAMOND: Record<string, HazardClassDiamondEntry> = {
  "1": { fill: { color: "#F97316", textColor: "#000000" }, name: "EXPLOSIVES" },
  "2": { name: "GASES" },
  "2.1": { fill: { color: "#C0392B", textColor: "#FFFFFF" }, name: "FLAMMABLE GAS" },
  "2.2": { fill: { color: "#16A34A", textColor: "#FFFFFF" }, name: "NON-FLAMMABLE GAS" },
  "2.3": { fill: { color: "#FFFFFF", textColor: "#000000" }, name: "POISON GAS" },
  "3": { fill: { color: "#C0392B", textColor: "#FFFFFF" }, name: "FLAMMABLE LIQUID" },
  "4.1": { name: "FLAMMABLE SOLID" },
  "4.2": { name: "SPONTANEOUSLY COMBUSTIBLE" },
  "4.3": { fill: { color: "#2563EB", textColor: "#FFFFFF" }, name: "DANGEROUS WHEN WET" },
  "5.1": { fill: { color: "#FACC15", textColor: "#000000" }, name: "OXIDIZER" },
  // 5.2 (red-over-yellow) and 7 (yellow-over-white) are split placards, so
  // they follow the striped/split rule above and render text-only.
  "5.2": { name: "ORGANIC PEROXIDE" },
  "6": { name: "TOXIC OR INFECTIOUS" },
  "6.1": { fill: { color: "#FFFFFF", textColor: "#000000" }, name: "POISON" },
  "6.2": { fill: { color: "#FFFFFF", textColor: "#000000" }, name: "INFECTIOUS SUBSTANCE" },
  "7": { name: "RADIOACTIVE" },
  "8": { name: "CORROSIVE" },
  "9": { name: "MISCELLANEOUS" },
};

export function diamondForHazardClass(dotHazardClass: string): HazardClassDiamondEntry | undefined {
  const key = dotHazardClass.trim();
  return HAZARD_CLASS_DIAMOND[key] ?? HAZARD_CLASS_DIAMOND[key.split(".")[0]];
}
